import { http } from '@app/lib/api'
import type { PublicEventSummary, PublicSponsor } from '@shared/contracts/content'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { formatDate, formatDateTime } from './index'

/** Renders server-sanitised HTML (from the markdown renderer). */
export function Prose({ html, className = '' }: { html: string; className?: string }) {
  // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is produced server-side by our escaping markdown renderer
  return <div className={`prose ${className}`} dangerouslySetInnerHTML={{ __html: html }} />
}

export function EventCard({
  event,
  featured = false,
  headingLevel = 3,
}: {
  event: PublicEventSummary
  featured?: boolean
  /** Match the surrounding outline: 2 directly under a page title, 3 under a section heading. */
  headingLevel?: 2 | 3
}) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  const start = new Date(event.startsAt)
  const month = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: event.timezone,
  }).format(start)
  const day = new Intl.DateTimeFormat('en-US', { day: 'numeric', timeZone: event.timezone }).format(
    start,
  )
  return (
    <Link
      to={`/events/${event.slug}`}
      className={`card card--interactive event-card${featured ? ' event-card--featured' : ''}`}
    >
      {event.heroUrl ? (
        <img src={event.heroUrl} alt="" className="event-card__img" loading="lazy" />
      ) : null}
      <div className="event-card__body">
        <div className="event-card__date" aria-hidden="true">
          <span className="event-card__month">{month}</span>
          <span className="event-card__day">{day}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          {event.source === 'community' ? (
            <div className="tiny">
              <span className="badge badge--info">Community event</span>
              {event.organizerName ? (
                <span className="muted"> · Hosted by {event.organizerName}</span>
              ) : null}
            </div>
          ) : event.programName ? (
            <div className="tiny muted">{event.programName}</div>
          ) : null}
          <Heading
            className="card__title"
            style={{ marginBottom: '.25rem', fontSize: 'var(--bp-text-xl)' }}
          >
            {event.name}
          </Heading>
          <div className="small muted">
            {formatDateTime(event.startsAt, {
              weekday: 'long',
              hour: 'numeric',
              minute: '2-digit',
            })}
            {event.venueName ? ` · ${event.venueName}` : ''}
          </div>
          {event.summary ? (
            <p className="small mb-0" style={{ marginTop: '.5rem' }}>
              {event.summary}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  )
}

export function SponsorStrip({
  sponsors,
  title = 'Thank you to our sponsors',
}: {
  sponsors: PublicSponsor[]
  title?: string
}) {
  if (sponsors.length === 0) return null
  const groups = new Map<string, PublicSponsor[]>()
  for (const s of sponsors) groups.set(s.level, [...(groups.get(s.level) ?? []), s])
  return (
    <section className="section" aria-labelledby="sponsors-h">
      <div className="section__head">
        <h2 id="sponsors-h">{title}</h2>
        <Link to="/sponsors" className="small">
          Become a sponsor →
        </Link>
      </div>
      {[...groups.entries()].map(([level, list]) => (
        <div key={level} className="sponsor-group">
          <h3
            className="tiny muted"
            style={{ textTransform: 'uppercase', letterSpacing: '.08em', margin: 0 }}
          >
            {level.replace(/_/g, ' ')}
          </h3>
          <ul className="sponsor-grid" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {list.map((s) => {
              const inner = s.logoUrl ? (
                <img src={s.logoUrl} alt={s.name} loading="lazy" />
              ) : (
                <span>{s.name}</span>
              )
              return (
                <li key={s.id}>
                  {s.website && /^https?:\/\//.test(s.website) ? (
                    <a
                      href={s.website}
                      target="_blank"
                      rel="noreferrer"
                      className={`sponsor sponsor--${level}`}
                    >
                      {inner}
                      <span className="sr-only"> (website, opens in a new tab)</span>
                    </a>
                  ) : (
                    <span className={`sponsor sponsor--${level}`}>{inner}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </section>
  )
}

export function usePublic<T>(key: string, path: string) {
  return useQuery({
    queryKey: ['public', key],
    queryFn: () => http.get<T>(path),
    staleTime: 5 * 60_000,
  })
}

export function PublicPage({
  title,
  eyebrow,
  intro,
  children,
  wide,
}: {
  title: string
  eyebrow?: string
  intro?: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  return (
    <div className={`container${wide ? ' container--wide' : ''} stack--lg`}>
      <header className="public-page__head">
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {intro ? <div className="lead">{intro}</div> : null}
      </header>
      {children}
    </div>
  )
}

export function SessionList({
  sessions,
  timezone,
}: {
  sessions: {
    id: string
    title: string
    description: string | null
    kind: string
    stage: string | null
    location: string | null
    startsAt: string
    endsAt: string | null
  }[]
  timezone?: string
}) {
  let lastDay = ''
  return (
    <ol className="schedule">
      {sessions.map((s) => {
        const day = formatDate(s.startsAt)
        const showDay = day !== lastDay
        lastDay = day
        return (
          <li key={s.id} className="schedule__item">
            {showDay ? (
              <div className="schedule__day" aria-hidden="true">
                {day}
              </div>
            ) : null}
            <span className="sr-only">{day}, </span>
            <div className="schedule__row">
              <time className="schedule__time" dateTime={s.startsAt}>
                {formatDateTime(s.startsAt, {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: timezone,
                })}
                {s.endsAt
                  ? ` – ${formatDateTime(s.endsAt, { hour: 'numeric', minute: '2-digit', timeZone: timezone })}`
                  : ''}
              </time>
              <div>
                <div className="bold">{s.title}</div>
                <div className="small muted">
                  {[s.stage, s.location].filter(Boolean).join(' · ')}
                  {s.kind !== 'performance' ? ` · ${s.kind.replace(/_/g, ' ')}` : ''}
                </div>
                {s.description ? <p className="small mb-0">{s.description}</p> : null}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
